import {SerializedStyles} from '@emotion/react';
import {useTheme, css, Typography, PopperProps} from '@mui/material';
import * as React from 'react';

import {DlMuiPopper} from './DlMuiPopper';
import {GetStylesFunction} from '../common';
import {useMuiAnchor} from '../hooks';

type Props = {
  tooltip?: string | number | JSX.Element;
  maxWidth?: number;
  style?: React.CSSProperties;
  wrapperCss?: SerializedStyles;
  wrapperStyle?: React.CSSProperties;
  noPadding?: boolean;
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLSpanElement>;
  popperInputCss?: SerializedStyles;
  /** Popper placement of the tooltip; default 'bottom'. Use e.g. 'top-start' when the
   * default position would cover content below the anchor (dropdown options, ...). */
  placement?: PopperProps['placement'];
};

export const TooltipWrapperCore: React.FC<Props> = (props) => {
  const {
    children,
    tooltip,
    maxWidth,
    noPadding,
    style,
    wrapperCss,
    wrapperStyle,
    onClick,
    onDoubleClick,
    popperInputCss,
    placement,
    ...extraProps
  } = props;

  const hasTooltip = tooltip != null && tooltip !== '';
  const tooltipContent: string | JSX.Element = React.isValidElement(tooltip) ? tooltip : String(tooltip);

  const theme = useTheme();
  const styles = getStyles({theme, params: {maxWidth, noPadding}});

  const {mouseEventEnter, mouseEventLeave, open, anchorEl, forceCloseTooltip} = useMuiAnchor(hasTooltip);

  return (
    <Typography variant={'value'} css={styles.span} style={style} onClick={onClick} onDoubleClick={onDoubleClick}>
      <span
        {...extraProps}
        css={wrapperCss ?? styles.tooltipWrapper}
        style={wrapperStyle}
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        // the mouse usually stays over the anchor while interacting with it (opening a dropdown,
        // clicking a chip) — no mouseleave fires, so the tooltip would keep covering what the
        // interaction just opened. any press hides it; for anchors that take focus (inputs,
        // dropdowns) useMuiAnchor then also blocks it from reopening until the focus leaves,
        // for the rest it reappears on the next hover.
        onMouseDown={forceCloseTooltip}
      >
        {children}
      </span>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} inputCss={popperInputCss} open={open} placement={placement}>
        <Typography variant={'popper'} css={styles.tooltipSpan}>
          {tooltipContent}
        </Typography>
      </DlMuiPopper>
    </Typography>
  );
};
TooltipWrapperCore.displayName = 'TooltipWrapperCore';

const getStyles: GetStylesFunction = (options) => ({
  tooltipWrapper: css`
    display: block;
    padding: ${options?.params?.noPadding ? 0 : options?.theme?.spacing(1, 0)};
    max-width: 100%;
  `,
  span: css`
    display: inline-block;
    max-width: 100%;
  `,
  tooltipSpan: css`
    max-width: ${options?.params?.maxWidth ? `${options?.params?.maxWidth}px` : 'none'};
    white-space: pre-line;
  `,
});
